import { ID } from '@datorama/akita';
import { IEntity } from '../interfaces/models';
import { Entity, Column } from '../entity/index';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { getBrowserLang } from '@ngneat/transloco';

export interface IAttachment extends IEntity {
  attachment: string;
  refId: number;
  createdBy: string;
  createdAt: Date;
  fileName: string;
  modifiedAt: Date;
  modifiedBy: string;
  refTable: string;
  url: string;
}

@Entity({
  name: PageTitles.attachment,
  language: LanguagePage.attachment,
})
export class Attachment implements IAttachment {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  type = 'Attachment';

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  createdBy: string;

  id: ID;
  attachment: string;
  refId: number;
  refTable: string;
  url: string;
  modifiedAt: Date;
  fileName: string;
  modifiedBy: string;
  createdAt: Date;
  dropPermissionName: string;

  constructor(
    att: IAttachment = {
      id: '123',
      attachment: '',
      refId: 0,
      createdBy: '',
      modifiedBy: '',
      fileName: '',
      modifiedAt: new Date(),
      createdAt: new Date(),
      refTable: '',
      url: '',
    },
    dropPermissionName: string = ''
  ) {
    const {
      id,
      attachment,
      refId,
      refTable,
      modifiedAt,
      createdBy,
      modifiedBy,
      createdAt,
      fileName,
    } = att;

    this.id = id;
    this.attachment = attachment;
    this.refId = refId;
    this.refTable = refTable;
    this.modifiedAt = modifiedAt;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.fileName = fileName;
    this.dropPermissionName = dropPermissionName;
    const blob = new Blob(this.getByteArray(attachment), {
      type: 'application/pdf',
    });

    // Create Blog URL
    this.url = window.URL.createObjectURL(blob);
  }

  @Column({
    type: ColumnType.raw,
    table: {
      defaultPosition: 3,
    },
  })
  get attachmentLink(): string {
    const a = document.createElement('a');
    const linkText = document.createTextNode('Download PDF');
    a.target = '_blank';
    a.download = this.fileName;
    a.appendChild(linkText);
    a.href = this.url;
    return a.outerHTML;
  }

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  get attachmentName(): string {
    return this.dropPermissionName;
  }

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
  })
  get modifiedAtFormatted() {
    if (!this.modifiedAt) {
      return '';
    }
    const dropCreatedDate = new Date(this.modifiedAt);
    const monthFormatter = new Intl.DateTimeFormat(getBrowserLang(), {
      month: 'short',
    });
    const monthName = monthFormatter.format(dropCreatedDate);
    const date = [
      `${monthName.charAt(0).toLocaleUpperCase()}${monthName
        .substr(1)
        .toLocaleLowerCase()}`,
      ' ',
      `${dropCreatedDate.getDate()}`.padStart(2, '0'),
      ', ',
      dropCreatedDate.getFullYear(),
      ' ',
      `${dropCreatedDate.getHours()}`.padStart(2, '0'),
      ':',
      `${dropCreatedDate.getMinutes()}`.padStart(2, '0'),
      ' ',
      dropCreatedDate.getHours() >= 12 ? 'PM' : 'AM',
    ];
    return `${date.join('')}`;
  }

  getByteArray(attachmentEncodedString = '', sliceSize = 512) {
    const byteCharacters = atob(attachmentEncodedString);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return byteArrays;
  }
}
